import { ErrorMessage } from '@hookform/error-message'
import { useFormContext } from 'react-hook-form'
import { useRef, useEffect, useState, useContext } from 'react'
import { useTranslation } from 'next-i18next'
import PropTypes from 'prop-types'
import Sortable from 'sortablejs'

// Internal components
import Svg from '../Svg/Svg'
import Button from '../Button/Button'
import FormError from '../FormError/FormError'

// Utils
import { throttle } from '../../utils/helpers/throttle'
import { UserContext } from '../../context/UserContext'

const emptyClass = ' not-empty'
const revealClass = ' is-revealed'
const activeClass = ' is-active'

const FormItemOrderListItem = (props) => {
  const { option, index, removeItem } = props

  const { register, watch, setValue } = useFormContext()
  const userContext = useContext(UserContext)

  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )

  // State
  const [durationLabel, setDurationLabel] = useState(`${option.durationLabel} ${t('DATE_MINUTE_ABBR')}`)

  const textAreaRef = useRef(null)
  const { ref, ...rest } = register(option?.input?.name)
  const itemInputValue = option?.input?.name ? watch(option.input.name) : false

  const onDurationChange = (e) => setDurationLabel(`${e.target.value} ${t('DATE_MINUTE_ABBR')}`)

  const resetTextareaHeight = () => {
    if (!textAreaRef?.current) return
    const el = textAreaRef.current
    if (!el) return
    if (el.clientHeight < el.scrollHeight) el.style.setProperty('--height', el.scrollHeight + 10 + 'px')
  }

  useEffect(() => {
    if (!textAreaRef?.current) return
    textAreaRef?.current?.addEventListener('input', () => resetTextareaHeight())
    return () => textAreaRef?.current?.removeEventListener('input', () => resetTextareaHeight())
  }, [])

  useEffect(() => {
    if (option?.input?.name) setValue(option.input.name, t(option.input.value))
  }, [])

  useEffect(() => {
    if (!textAreaRef?.current) return
    setTextareaHeight()
    window.addEventListener('resize', setTextareaHeight)
    return () => window.removeEventListener('resize', setTextareaHeight)
  }, [])

  const setTextareaHeight = throttle(() => {
    if (!textAreaRef?.current) return
    const el = textAreaRef.current
    el.style.height = '1px'
    el.style.setProperty('--height', el.scrollHeight + 2 + 'px')
    el.style.height = 'var(--height)'
  }, 100)

  return (
    <div className="FormItem-orderlistItem" key={`FormItem-orderlistItem--${index + 1}`}>
      {option.controls && (
        <div className="FormItem-orderlistControl">
          <Svg
            id="icon-chevron-up"
            modifiers={['FormItem-orderlistControlIcon', 'FormItem-orderlistControlIcon--up']}
          />
          <Svg
            id="icon-chevron-down"
            modifiers={['FormItem-orderlistControlIcon', 'FormItem-orderlistControlIcon--down']}
          />
        </div>
      )}
      <div className="FormItem-orderlistContents">
        {option.input ? (
          <div className={`FormItem FormItem--orderlistInput ${itemInputValue ? emptyClass : ''}`}>
            <label className="FormItem-label" htmlFor={option.input.name}>{`${
              option.input.transl ? t(option.input.transl) : option.input.label
            }`}</label>
            {option.input.type === 'textarea' && (
              <textarea
                id={option.input.name}
                defaultValue={t(option.input.value) || ''}
                name={option.input.name}
                placeholder={`${option.input.transl ? t(option.input.transl) : option.input.label}`}
                className={`FormItem-input FormItem-input--${option.input.type}`}
                disabled={option?.disabled === true ? 'disabled' : ''}
                ref={(e) => {
                  ref(e)
                  textAreaRef.current = e
                }}
                {...rest}
              />
            )}
          </div>
        ) : (
          <div className="FormItem-orderlistCopy">
            <span className="FormItem-orderlistLabel">{t(option.label)}</span>
            <span className="FormItem-orderlistTitle">{option.title}</span>
          </div>
        )}
        <input
          type="hidden"
          id={option.id}
          defaultValue={index}
          name={option.name}
          className="FormItem-input"
          {...register(option.name)}
        />
      </div>
      {option.durationLabel && (
        <div className="FormItem-orderlistDuration">
          <label htmlFor="" className="FormItem-orderlistDurationLabel">
            <div className="FormItem-orderlistDurationCopy">{t(durationLabel)}</div>
            <div className="FormItem-orderlistDurationSymbol">
              <Svg id="icon-chevron-down" modifiers={['FormItem-orderlistDurationIcon']} />
            </div>
            <select
              id={`exerciseDurationId_${option.id}`}
              defaultValue={option.durationActive}
              name={`exerciseDurationName_${option.value}`}
              className="FormItem-orderlistDurationSelect"
              {...register(`exerciseDurationName_${option.value}`)}
              onChange={(e) => onDurationChange(e)}
            >
              {option.durationOptions.length &&
                option.durationOptions.map((durationOption, index) => {
                  return (
                    <option value={durationOption.value} key={`FormItem-durationOption-${option.id}_${index + 1}`}>
                      {`${durationOption.label} ${t('DATE_MINUTE_ABBR')}`}
                    </option>
                  )
                })}
            </select>
            <input
              type="hidden"
              name={`exerciseDurationDefault_${option.value}`}
              defaultValue={option.durationValue}
              {...register(`exerciseDurationDefault_${option.value}`)}
            />
          </label>
        </div>
      )}
      {option.remove && option?.disabled !== true && (
        <div
          className="FormItem-orderlistRemove"
          onClick={(e) => {
            e.target.parentNode.classList.add('is-filtered')
            removeItem(option)
          }}
        >
          <div className="FormItem-orderlistRemoveSymbol">
            <Svg id="icon-close" modifiers={['FormItem-orderlistRemoveIcon']} />
          </div>
        </div>
      )}
    </div>
  )
}

FormItemOrderListItem.propTypes = {
  option: PropTypes.any,
  index: PropTypes.any,
  removeItem: PropTypes.any
}

const FormItemOrderlist = (props) => {
  const { item, onFormItemLoad, groupedState, revealState } = props
  const { watch, errors, setValue, formState, unregister, trigger } = useFormContext()
  const userContext = useContext(UserContext)
  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )

  // State
  const [isGrouped, setIsGrouped] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const [propsItems, setPropsItems] = useState(item.options)
  const [lastItemCache, setlastItemCache] = useState(null)
  const [emptyStateMessage, setEmptyStateMessage] = useState('')

  // Refs
  const itemRef = useRef()
  const sortableRef = useRef()

  const itemValue = watch(item.name)
  const itemProps = item
  const sortableProps = item?.type === 'orderlist' ? { 'data-sortable': item.name } : ''

  useEffect(() => {
    if (itemValue) trigger()
    if (isLoaded === false && itemValue) {
      onFormItemLoad({ itemProps, itemRef, itemValue })
      setIsLoaded(true)
    }
  }, [itemValue])

  useEffect(() => {
    if (item?.group && groupedState) {
      const itemLabel = item?.group?.label
      const groupedItem = groupedState.filter((str) => {
        return str.label.indexOf(itemLabel) >= 0
      })
      setIsGrouped(groupedItem[0].active)
    }
  }, [groupedState])

  useEffect(() => {
    if (item?.type === 'orderlist' && sortableRef.current) {
      Sortable.create(sortableRef.current, {
        handle: '.FormItem-orderlistControl',
        filter: '.FormItem-orderlistRemove',
        animation: 150,
        onSortStart: (_, event) => event.preventDefault(), // Fix for IOS/Safari issue
        onUpdate: () => {
          Array.from(sortableRef.current.querySelectorAll('.FormItem-input[type="hidden"]')).forEach((input, i) => {
            setValue(input.name, parseInt(i))
          })
        }
      })
    }
  }, [])

  useEffect(() => {
    let message = ''
    // TODO make this more dynamic from modal API
    message = item.name === 'warmupOrder' ? t('FORMITEM__NOTICE_WARMUP_ORDER') : message
    message = item.name === 'trainingsOrder' ? t('FORMITEM__NOTICE_TRAINING_ORDER') : message
    message = item.name === 'exercisesOrder' ? t('FORMITEM__NOTICE_EXERCISE_ORDER') : message
    message = item.name === 'exercisesRules' ? t('FORMITEM__NOTICE_EXERCISE_RULES') : message
    message =
      item.name === 'exercisesStepsEasier' || item.name === 'exercisesStepsHarder'
        ? t('FORMITEM__NOTICE_DIFFICULTY_STEPS')
        : message
    setEmptyStateMessage(message)
  }, [item])

  const setNewItem = (newItem, cache, el, itemCount) => {
    newItem.id = `${el.name.slice(0, -1)}${itemCount}`
    newItem.name = `${el.name.slice(0, -1)}${itemCount}`

    // Input name needs to be split since it now also accepts a UUID and Date in the same string.
    // This is so multiple players can be updated to your team.
    const splitName = el.name.split('::')

    if (splitName.length > 1) {
      newItem.name = `${splitName[0].slice(0, -1)}_${itemCount}`
    }

    newItem.input = el?.input.transl ? {} : false
    if (newItem.input) {
      const splitInputName = el.input.name.split('::')
      newItem.input.transl = el.input.transl

      // Conditional for create match modal orderlist
      if (splitInputName?.length > 1) {
        newItem.input.name = `${splitInputName[0]}_${itemCount}`
      } else {
        newItem.input.name = `${el.input.name.slice(0, -1)}${itemCount}`
      }
      newItem.input.required = el.input.required
      newItem.input.value = ''
      newItem.input.type = 'textarea'
      newItem.input.disabled = false
    }
    newItem.transl = cache.length !== 0 ? el.transl : cache.transl
    newItem.remove = cache.length !== 0 ? el.remove : cache.remove

    // Set the transl and remove when adding a new item based on no previous items
    if (newItem.name.includes('guest_playerItem_')) {
      newItem.transl = 'MDL__LBL_GUESTNAME'
      newItem.remove = true
    }
  }

  const addItemOptionsOnClick = (params) => {
    const newItem = {}
    const state = []
    const cache = propsItems
    const existingCache = []
    const missingCache = []
    let itemCount = 0

    cache.forEach((option, index) => {
      state.push(option)
      itemCount = index
      // Get all the fields
      if (option.name.split('::').length > 2) {
        existingCache.push(parseInt(option.input.name.slice(-1)))
      } else {
        existingCache.push(parseInt(option.name.slice(-1)))
      }
    })

    // Get all missing numbers
    for (let i = 1; i <= cache.length; i++) {
      if (existingCache.indexOf(i) === -1) {
        missingCache.push(i)
      }
    }
    itemCount = cache.length !== 0 ? itemCount + 1 : 0

    // Set the item count to the missing number so duplicates are avoided
    if (existingCache.includes(itemCount)) {
      itemCount = missingCache[0]
    }

    setNewItem(newItem, cache, cache.length !== 0 ? cache[0] : lastItemCache, itemCount)

    if (!newItem) return false

    state.push(newItem)
    setPropsItems(state)
    setlastItemCache(null)
  }

  const removeItem = (option) => {
    const index = option.name.charAt(option.name.length - 1)
    if (parseInt(index) === 0) {
      setlastItemCache(option)
    }

    unregister(`exerciseDurationName_${option.name}`)
    if (option?.name) unregister(option.name)
    if (option?.input?.name) unregister(option.input.name)

    let state = []
    const cache = propsItems
    state = cache.filter((optionItem) => optionItem.name !== option.name)

    setPropsItems(state)
  }

  return (
    <div
      ref={itemRef}
      className={`FormItem FormItem--${item.type}${item.modifier ? ` FormItem--${item.modifier}` : ''}${
        itemValue ? emptyClass : ''
      }${revealState ? revealClass : ''}${!isGrouped ? '' : activeClass}`}
      data-reveal-target={item.revealed}
      {...sortableProps}
    >
      {item.heading && <h3 className="FormItem-orderlistTypeHeading">{t(item.heading)}:</h3>}
      <div className="FormItem-orderlistGroup" ref={sortableRef} data-empty-state={emptyStateMessage}>
        {propsItems.length !== 0 &&
          propsItems.map((option, index) => {
            return (
              <FormItemOrderListItem
                option={option}
                index={index}
                removeItem={removeItem}
                key={`formItemOrderListItem-${index}-${option.name}`}
              />
            )
          })}
      </div>
      {formState.isSubmitted && <ErrorMessage errors={errors} name={item.name} as={<FormError />} />}
      {item.button && (
        <Button
          icon={item.button.icon}
          label={t(item.button.label)}
          modifiers={item.button.modifiers}
          onClick={() => {
            addItemOptionsOnClick()
          }}
        />
      )}
    </div>
  )
}

FormItemOrderlist.propTypes = {
  item: PropTypes.object,
  onFormItemLoad: PropTypes.func,
  revealState: PropTypes.any,
  groupedState: PropTypes.any
}

export default FormItemOrderlist
